/**
 * CollectNotice — notice d'information sur la collecte de données.
 *
 * V13.34 (2026-10-27) — composant unifié utilisé au point de collecte des
 * formulaires conformément à la section H du bloc juridique autonome
 * fourni par l'éditrice (Anna Yon Yue Chong).
 *
 * Règles :
 *   - jamais de case « J'accepte le RGPD » ;
 *   - les CGU sont acceptées distinctement par une case obligatoire
 *     (séparée de la notice) sur la création de compte ;
 *   - la confidentialité reste une information accessible avant collecte,
 *     pas un consentement global ;
 *   - les cases facultatives sont séparées par finalité, non précochées,
 *     révocables et sans effet sur les fonctions qui n'en dépendent pas.
 *
 * Les variantes :
 *   - "signup"       : création de compte
 *   - "profile"      : publication / aperçu avant publication
 *   - "image"        : envoi d'une image
 *   - "claim"        : revendication ou validation d'affiliation
 *   - "rights"       : correction, opposition, signalement, droits
 *   - "contribution" : contribution externe (actualité, résultat)
 *   - "ai"           : assistance IA avant envoi d'une requête
 *   - "contact"      : formulaire de contact général
 */
import { Link } from "react-router-dom";

const TEXT = {
  signup: (
    <>
      Les champs obligatoires servent à créer et sécuriser ton compte et à fournir
      les fonctions demandées. Ton courriel de connexion n'est pas affiché sur ta
      fiche. Responsable : Anna Yon Yue Chong. Pour connaître les destinataires,
      les durées et tes droits :{" "}
      <Link to="/legal/confidentialite" className="underline">
        Politique de confidentialité
      </Link>
      . Contact :{" "}
      <a href="mailto:Anna.yyc@gmail.com" className="underline">
        Anna.yyc@gmail.com
      </a>
      .
    </>
  ),
  profile: (
    <>
      Seuls les champs annoncés comme publics seront visibles sur internet.
      Vérifie cet aperçu avant publication. Ne publie ni adresse privée, ni
      document d'identité, ni donnée médicale. Les coordonnées de ton compte
      restent privées. Voir la{" "}
      <Link to="/legal/confidentialite" className="underline">
        Politique de confidentialité
      </Link>
      .
    </>
  ),
  image: (
    <>
      En envoyant une image, tu confirmes disposer des droits nécessaires et des
      accords requis des personnes identifiables. L'autorisation d'affichage est
      limitée à ta fiche AITO.PF et à ses présentations à l'intérieur du site.
      Les règles figurent dans la page{" "}
      <Link to="/legal/images" className="underline">
        Images et autorisations
      </Link>
      . Pour un mineur, la publication reste en attente jusqu'à la vérification
      des autorisations des représentants légaux —{" "}
      <Link to="/legal/mineurs" className="underline">
        Protection des mineurs
      </Link>
      .
    </>
  ),
  claim: (
    <>
      Les données saisies servent à vérifier ton identité ou ton habilitation et
      à traiter ta demande. Elles ne sont pas affichées publiquement. N'envoie
      pas spontanément de pièce d'identité ; un élément limité peut être
      demandé en cas de doute raisonnable. Voir la{" "}
      <Link to="/legal/confidentialite" className="underline">
        Politique de confidentialité
      </Link>
      . Contact :{" "}
      <a href="mailto:Anna.yyc@gmail.com" className="underline">
        Anna.yyc@gmail.com
      </a>
      .
    </>
  ),
  rights: (
    <>
      Les informations servent à examiner ta demande, te répondre et conserver
      une preuve limitée de son traitement. Elles ne sont pas publiques et ne
      servent pas à la prospection. Tu peux agir sans créer de compte. Consulte
      la{" "}
      <Link to="/legal/confidentialite" className="underline">
        Politique de confidentialité
      </Link>{" "}
      ou écris à{" "}
      <a href="mailto:Anna.yyc@gmail.com" className="underline">
        Anna.yyc@gmail.com
      </a>
      . Aucune acceptation des CGU ou consentement RGPD n'est requis pour
      recevoir une demande de droits.
    </>
  ),
  contribution: (
    <>
      Indique tes sources et vérifie les faits. Ne transmets pas d'informations
      privées ou sensibles concernant une autre personne. Une contribution est
      examinée selon la{" "}
      <Link to="/legal/contributeurs" className="underline">
        Charte des contributeurs
      </Link>
      .
    </>
  ),
  ai: (
    <>
      Assistance IA. Vérifie les faits et les sources avant d'utiliser la
      proposition. N'ajoute aucune coordonnée privée, aucun justificatif,
      aucune donnée médicale ni donnée personnelle de mineur. Détails :{" "}
      <Link to="/legal/ia" className="underline">
        Intelligence artificielle
      </Link>
      .
    </>
  ),
  contact: (
    <>
      Les informations servent à traiter ta demande et conserver une preuve
      limitée de son suivi. Elles ne sont pas publiques et ne servent pas à la
      prospection.{" "}
      <Link to="/legal/confidentialite" className="underline">
        Politique de confidentialité
      </Link>{" "}
      ·{" "}
      <a href="mailto:Anna.yyc@gmail.com" className="underline">
        Anna.yyc@gmail.com
      </a>
      .
    </>
  ),
};

/**
 * @param {{ variant: keyof typeof TEXT, className?: string, testId?: string }} props
 */
export function CollectNotice({ variant, className = "", testId }) {
  const body = TEXT[variant];
  if (!body) return null;
  return (
    <p
      className={`text-[11px] leading-relaxed text-muted-foreground ${className}`.trim()}
      data-testid={testId || `collect-notice-${variant}`}
    >
      {body}
    </p>
  );
}

export default CollectNotice;
